/**
 * Lane C0-OUTFIT2X step 1: the player's starter pair (the default man and woman: looks hero*, no school, no fashion)
 * drawn natively at the figure grid in full detail, and told apart by silhouette, hair, face and colour use.
 *
 * Ported, not invented: both wear the starter coat's colour (the equipped coat, coatNavy: navy with a gold edge; the man
 * in warm white instead, user 10-11 question 003) over
 * the hero's warm-white inner robe, cut from the existing parts library —
 * - the man: a knee-length travelling robe (`travel`: narrow sleeves, split front) over ink trousers and leather boots,
 *   a leather belt with a gold buckle over a maroon sash, a 回纹 key-fret hem in gold on maroon, maroon cuffs, frog
 *   buttons, a jade disc pendant with a red tassel on the hip, a topknot under a small gold crown (no ribbons);
 * - the woman: the high-waisted ruqun (`ruqun`: wide sleeves, a long skirt to the ankles) in figured navy silk, a red
 *   silk sash tied in a bow under the chest, a cloud-curl hem in gold on warm white, embroidered slippers, a long
 *   jade waist pendant (禁步), long hair up in a high bun with a gold hairpin, a plum flower and dangling beads.
 */
import type { Look } from '../../sprites';
import { bottomLayer, robeLayer, topLayer } from '../clothes';
import type { Layer } from '../figure';
import { shoesLayer } from '../gear';
import { type Colours, type Dress, nativeLayer } from './garments';
import { nativeHairLayer } from './hair2x';
import { ornamentLayer } from './ornaments';
import { BLOSSOM, CLOUD, FRET, mat } from './paint';

/** The player's own looks without a school uniform or a fashion outfit. */
export const isStarter = (look: Look): boolean => !!look.faceId?.startsWith('hero') && !look.school && !look.fashion;

const WHITE = '#efe7d6';
const MAROON = '#6a1e2a';
const RED = '#b8283a';
const GOLD = '#d6a640';
const INK = '#2c2834';
const LEATHER = '#4a2e20';

const gold = mat(GOLD, 'gold');

/** The coat the starter wears (the equipped coat), else the hero's navy. */
const NAVY = { c: '#1d2a4f', s: '#131c38', edge: '#d9b46c', len: 9 };
const coatOf = (look: Look) => look.coat ?? NAVY;

/**
 * The man's starter colours (the user, question 003, 2026-10-11: the colour split of docs/design/costume/img/
 * redesign-starter.png): warm white with the maroon sash, cuffs and key-fret hem, where the woman keeps navy + red.
 * The starter navy coat (or none) reads as this warm white on him; any other coat he puts on keeps its own colour.
 */
const WARM_WHITE = { c: '#e6dccb', s: '#c4b59c', edge: MAROON, len: 9 };
const manCoatOf = (look: Look) => (!look.coat || look.coat.c === NAVY.c ? WARM_WHITE : look.coat);

function man(look: Look): Layer[] {
  const coat = manCoatOf(look);
  // Starter vs sect uniform (docs/design/costume/2026-10-11-starter-vs-uniform.md, option 1): ink leg wraps, a maroon
  // collar and front edge with gold piping, and no leather belt over the maroon sash, so the four maroon marks (collar,
  // sash, cuffs, hem) read apart from the white sect uniform.
  const trousers: Colours = { cloth: INK, wrap: INK };
  const boots: Colours = { cloth: LEATHER };
  const shirt: Colours = { cloth: WHITE, trim: MAROON, inner: WHITE, accent: RED };
  const robe: Colours = { cloth: coat.c, trim: MAROON, inner: WHITE, sash: MAROON, accent: GOLD };
  const robeDress: Dress = {
    mats: { cloth: mat(coat.c, 'cotton'), trim: mat(MAROON, 'silk'), inner: mat(WHITE, 'cotton'), sash: mat(MAROON, 'silk'), accent: gold },
    hem: { motif: FRET, ground: mat(MAROON, 'silk'), thread: gold, piping: gold },
    cuff: { ground: mat(MAROON, 'silk'), piping: gold, rows: 4 },
    piping: gold,
    folds: 'folds',
  };
  return [
    nativeLayer((s) => bottomLayer({ id: 'trousers', colour: s.cloth!, wrap: s.wrap! }), trousers, {
      mats: { cloth: mat(INK, 'cotton'), wrap: mat(INK, 'cotton') },
    }),
    nativeLayer((s) => shoesLayer({ id: 'boots', colour: s.cloth! }), boots, { mats: { cloth: mat(LEATHER, 'leather') } }),
    nativeLayer(
      (s) => topLayer({ id: 'bracer', colour: s.cloth!, trim: s.trim!, inner: s.inner!, accent: s.accent! }),
      shirt,
      { mats: { cloth: mat(WHITE, 'cotton'), trim: mat(MAROON, 'silk'), inner: mat(WHITE, 'cotton'), accent: mat(RED, 'silk') } },
    ),
    nativeLayer(
      (s) =>
        robeLayer({ id: 'travel', colour: s.cloth!, trim: s.trim!, inner: s.inner!, sash: s.sash!, accent: s.accent!, slitFill: 'fold' }),
      robe,
      robeDress,
    ),
    nativeHairLayer('hightail', look.hair, { noRibbon: true }),
    ornamentLayer({ crown: true, frogs: true, pendant: 'hip' }),
  ];
}

function woman(look: Look): Layer[] {
  const coat = coatOf(look);
  const robe: Colours = { cloth: coat.c, trim: GOLD, inner: WHITE, sash: RED, accent: WHITE };
  const shoes: Colours = { cloth: RED };
  const robeDress: Dress = {
    mats: { cloth: mat(coat.c, 'silk'), trim: gold, inner: mat(WHITE, 'silk'), sash: mat(RED, 'silk'), accent: mat(WHITE, 'silk') },
    hem: { motif: CLOUD, ground: mat(WHITE, 'silk'), thread: gold, piping: gold },
    cuff: { ground: mat(WHITE, 'silk'), piping: gold, rows: 5 },
    folds: 'folds',
    brocade: { motif: BLOSSOM, sx: 12, sy: 10 },
    piping: gold,
    plainSkirt: true,
  };
  return [
    nativeLayer((s) => shoesLayer({ id: 'slippers', colour: s.cloth! }), shoes, { mats: { cloth: mat(RED, 'silk') } }),
    nativeLayer(
      (s) => topLayer({ id: 'cross', colour: s.cloth!, trim: s.trim!, inner: s.inner! }),
      { cloth: WHITE, trim: GOLD, inner: WHITE },
      { mats: { cloth: mat(WHITE, 'silk'), trim: gold, inner: mat(WHITE, 'silk') } },
    ),
    nativeLayer(
      (s) =>
        robeLayer({ id: 'ruqun', colour: s.cloth!, trim: s.trim!, inner: s.inner!, sash: s.sash!, accent: s.accent! }),
      robe,
      robeDress,
    ),
    nativeHairLayer('halfup', look.hair),
    ornamentLayer({ hairpin: true, pendant: 'long' }),
  ];
}

/** The starter's clothes, hair and ornaments as native layers (sets.ts `appearanceOf` adds body, face, weapon). */
export function starterLayers(look: Look): Layer[] {
  return look.female ? woman(look) : man(look);
}
